import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import {
  operations as curriculumCourseOperations,
  selectors as curriculumCourseSelectors,
} from 'store/curriculumCourseManager'
import {
  operations as lmsOperations,
  selectors as lmsSelectors,
} from 'store/lmsManager'
import { useUserContext } from 'layers/content/v2/hooks/useUserContext'
import { transformCurriculumCourseTree } from 'layers/content/v3/transforms/transformCurriculumCourseTree'

/**
 * Normalises a coursePointer.query object that may use either camelCase keys
 * (curriculum-service schema) or snake_case keys (wire format).
 */
function parseCoursePointerQuery(coursePointer) {
  const q = coursePointer?.query || {}
  return {
    courseCode: q.courseCode || q.course_code || null,
    contentGroupVersionId:
      q.contentGroupVersionId ?? q.content_group_version_id ?? null,
    programId: q.programId ?? q.program_id ?? null,
    draftMode: q.draftMode || q.draft_mode || null,
  }
}

/**
 * Resolves the active siteId from an explicit value, userContext, or URL param.
 */
function resolveSiteId({ explicitSiteId, userContext }) {
  if (explicitSiteId != null) return explicitSiteId
  const fromContext = userContext?.sites?.[0]?.siteId
  if (fromContext != null) return fromContext
  const search =
    typeof window !== 'undefined' ? window?.location?.search || '' : ''
  const query = new URLSearchParams(search)
  const fromUrl = query.get('siteId') || query.get('site')
  if (!fromUrl) return null
  const parsed = Number(fromUrl)
  return Number.isNaN(parsed) ? null : parsed
}

/**
 * Traverses a transformed course tree for the first node whose leafNodeId
 * starts with courseNodeId. Leaf node IDs are composite strings of the form
 * "<externalId>-<presentationExternalId>", so courseNodeId is a prefix.
 */
function findLeafNodeId(treeNode, courseNodeId) {
  if (!treeNode || !courseNodeId) return null
  if (treeNode.leafNodeId?.startsWith(courseNodeId)) {
    return treeNode.leafNodeId
  }
  for (const child of treeNode.children || []) {
    const found = findLeafNodeId(child, courseNodeId)
    if (found) return found
  }
  return null
}

/**
 * Checks whether a specific lesson node is marked done for a given registration.
 */
function checkIsComplete({
  siteEducator,
  registrationId,
  courseNodeId,
  leafNodeId,
}) {
  if (!Array.isArray(siteEducator) || !courseNodeId) return false
  const match =
    registrationId != null
      ? siteEducator.find(
          reg => String(reg.registration_id) === String(registrationId),
        )
      : null
  if (!match || !Array.isArray(match.lesson_nodes)) return false
  return match.lesson_nodes.some(node => {
    if (node.course_node_id === courseNodeId) return !!node.is_done
    if (leafNodeId && node.leaf_node_id === leafNodeId) return !!node.is_done
    return false
  })
}

/**
 * Encapsulates all LMS and curriculum-service logic for the "Mark Complete"
 * button on IndividualResourceV3 learning experiences.
 *
 * Flow:
 *  1. Fetches the course tree from /v1/audienced-course-versions via coursePointer
 *  2. Derives leafNodeId by traversing the tree with a prefix match on courseNodeId
 *  3. Fetches /v2/instructor/me and reads the instructorDataKey slice
 *  4. Matches the registration by course_name === coursePointer.query.course_code
 *  5. handleMarkComplete POSTs to /v2/attempt without userContextB64, then refreshes
 *
 * @param {Object}      options
 * @param {Object}      options.coursePointer      - coursePointer from routeContent
 * @param {string}      options.courseNodeId        - entry point from resource_metadata
 * @param {string}      options.contentGroupCode    - e.g. 'dwb' — used as the state-key
 *                                                    prefix when caching the course tree
 * @param {string}      options.instructorDataKey   - top-level key in instructor/me
 *                                                    response (e.g. 'Dwb')
 * @param {number|string|null} [options.siteId]    - explicit override; resolved from
 *                                                    userContext/URL if omitted
 */
export function useMarkCompleteV3({
  coursePointer,
  courseNodeId,
  contentGroupCode,
  instructorDataKey,
  siteId: explicitSiteId = null,
}) {
  const dispatch = useDispatch()
  const userContext = useUserContext()
  const location = useLocation()

  const { courseCode } = useMemo(() => parseCoursePointerQuery(coursePointer), [
    coursePointer,
  ])

  const siteId = useMemo(() => resolveSiteId({ explicitSiteId, userContext }), [
    explicitSiteId,
    userContext,
  ])

  // --- Course tree ---

  const courseTreeEntry = useSelector(
    curriculumCourseSelectors.selectCurriculumCourseTree(
      contentGroupCode,
      courseCode,
      siteId,
    ),
  )

  useEffect(() => {
    if (!coursePointer || !courseCode || siteId == null) return
    if (courseTreeEntry?.data || courseTreeEntry?.loading) return
    dispatch(
      curriculumCourseOperations.fetchCourseTreeFromPointer({
        coursePointer,
        contentGroupCode,
        siteId,
      }),
    )
  }, [coursePointer, courseCode, contentGroupCode, siteId, dispatch])

  const leafNodeId = useMemo(() => {
    if (!courseTreeEntry?.data || !courseNodeId) return null
    const transformedTree = transformCurriculumCourseTree(courseTreeEntry.data)
    if (!transformedTree) return null
    const found = findLeafNodeId(transformedTree, courseNodeId)
    if (!found) {
      console.error(
        `[useMarkCompleteV3] courseNodeId "${courseNodeId}" not found in course tree (contentGroupCode: "${contentGroupCode}")`,
      )
      return null
    }
    return found
  }, [courseTreeEntry, courseNodeId, contentGroupCode])

  // --- Instructor / registration ---

  const isFetchingOrCreating = useSelector(
    state => !!state.lmsManager?.isFetchingOrCreatingProgramFlex,
  )
  const instructorData = useSelector(
    lmsSelectors.selectInstructorData(instructorDataKey),
  )

  useEffect(() => {
    if (isFetchingOrCreating || instructorData) return
    dispatch(lmsOperations.fetchInstructorProgramFlexGet())
  }, [dispatch, isFetchingOrCreating, instructorData])

  const classId = useMemo(() => {
    const search =
      location?.search ||
      (typeof window !== 'undefined' ? window?.location?.search || '' : '')
    const query = new URLSearchParams(search)
    return query.get('class')
  }, [location?.search])

  const registrationId = useMemo(() => {
    if (!instructorData || !classId) return null
    const siteEducatorArr = Array.isArray(instructorData.siteEducator)
      ? instructorData.siteEducator
      : []
    const match = siteEducatorArr.find(
      reg => String(reg.registration_id) === String(classId),
    )
    if (!match) {
      console.error(
        `[useMarkCompleteV3] no registration found for registration_id "${classId}" (instructorDataKey: "${instructorDataKey}")`,
      )
      return null
    }
    return match.registration_id ?? null
  }, [instructorData, classId, instructorDataKey])

  const isComplete = useMemo(
    () =>
      checkIsComplete({
        siteEducator: instructorData?.siteEducator,
        registrationId,
        courseNodeId,
        leafNodeId,
      }),
    [instructorData, registrationId, courseNodeId, leafNodeId],
  )

  // --- Mark complete handler ---

  const handleMarkComplete = async () => {
    if (isComplete) return
    if (!registrationId || !leafNodeId) {
      console.error(
        '[useMarkCompleteV3] registrationId and leafNodeId are required to mark complete',
      )
      return
    }
    await dispatch(
      lmsOperations.makeAttemptV2Direct({
        registrationId,
        leafNodeId,
        progress: 1,
      }),
    )
    await dispatch(lmsOperations.fetchInstructorProgramFlexGet())
  }

  return {
    isComplete,
    handleMarkComplete,
    registrationId,
    leafNodeId,
    loading: !!courseTreeEntry?.loading,
  }
}
